import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useSocketStore from "../../../stores/SocketStore";
import Chat from "../../Templates/Chats/Chat";
import { useKeycloak } from "@react-keycloak/web";
import i18n from "../../../i18n";

const authUrl = process.env.REACT_APP_AUTH_API_URL;

const DynamicChats = () => {
  const navigate = useNavigate();
  const { projectId, chatId } = useParams();
  const { keycloak } = useKeycloak();
  const accessToken = keycloak.token;

  const {
    sendMessage,
    sendContinueMessage,
    sendSystemContextHTTP,
    onMessage,
    connect,
  } = useSocketStore();

  const [pendingNavigate, setPendingNavigate] = useState(false);
  const [tempChatId, setTempChatId] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [overlay, setOverlay] = useState(null);
  const [codeInput, setCodeInput] = useState(false);
  const [fileInput, setFileInput] = useState(false);
  const [diff, setDiff] = useState(false);
  const [messages, setMessages] = useState([]);
  const [fileContent, setFileContent] = useState("");
  const [enabledTextArea, setEnabledTextArea] = useState(true);
  const [showFirstMessageSkeleton, setShowFirstMessageSkeleton] =
    useState(false);
  const [isMessageIncomplete, setIsMessageIncomplete] = useState(false);

  const handleReceiveMessageRef = useRef(null);

  const ensureToken = useCallback(async () => {
    try {
      if (!keycloak.isTokenExpired(5)) {
        return keycloak.token;
      }

      const updated = await keycloak.updateToken(5);

      if (updated) {
        return keycloak.token;
      } else {
        const retry = await keycloak.updateToken(5);
        if (retry) {
          return keycloak.token;
        }
      }

      console.error("Não foi possível atualizar o token após o retry.");
      return null;
    } catch (error) {
      console.error("Erro ao atualizar o token:", error);
      return null;
    }
  }, [keycloak]);

  useEffect(() => {
    // Always set default chat configuration for the given project
    setOverlay(null);
    setCodeInput(false);
    setFileInput(true);
    setDiff(false);
  }, [chatId, projectId]);

  const createChatId = async (projectId) => {
    // TODO: Padronizar o uso de axios para requisições
    const response = await fetch(`${authUrl}/chats/create/${projectId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });
    const data = await response.json();
    return data.chatId;
  };

  const handleSendMessage = useCallback(
    async (message, systemContext = null) => {
      const currentToken = await ensureToken();

      if (!currentToken) {
        console.error("Token de acesso não disponível");
        return;
      }

      const userMessage = { role: "user", content: message };
      setMessages((prevMessages) => [...prevMessages, userMessage]);
      setShowFirstMessageSkeleton(true);

      if (!chatId) {
        try {
          const newChatId = await createChatId(projectId);
          console.log("newChatId", newChatId);
          const infoContext = `
          {
            name: ${keycloak.idTokenParsed.given_name},
            idioma: ${i18n.language},
          }`;
          setTempChatId(newChatId);
          setPendingNavigate(true);
          await handleSendSystemContextHTTP(infoContext, newChatId);

          if (systemContext) {
            await handleSendSystemContextHTTP(systemContext, newChatId);
          }

          if (fileContent) {
            await handleSendSystemContextHTTP(fileContent, newChatId);
          }

          await sendMessage({
            chatId: Number(newChatId),
            message,
            token: currentToken,
          });
        } catch (error) {
          console.error("Failed to create chat or send message:", error);
        }
      } else {
        if (fileContent) {
          await handleSendSystemContextHTTP(fileContent, chatId);
        }

        sendMessage({
          chatId: Number(chatId),
          message,
          token: currentToken,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chatId, projectId, sendMessage, ensureToken, navigate, fileContent]
  );

  const handleSendContinueMessage = useCallback(async () => {
    if (!accessToken) {
      console.error("Token de acesso não disponível");
      return;
    }

    setIsMessageIncomplete(false);
    await sendContinueMessage(Number(chatId), accessToken);
  }, [accessToken, chatId, sendContinueMessage]);

  const handleSendSystemContextHTTP = async (systemContext, systemChatId) => {
    if (!accessToken) {
      console.error("Token de acesso não disponível");
      return;
    }

    await sendSystemContextHTTP(systemChatId, systemContext);
  };

  const getMessagesByChat = useCallback(async (chatId) => {
    if (!accessToken) {
      console.error("Token de acesso não disponível");
      return [];
    }

    // TODO: Padronizar o uso de axios para requisições
    const response = await fetch(`${authUrl}/chats/${chatId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch messages");
    }

    const data = await response.json();

    return data.messages
      .map((apiMessage) => ({
        id: apiMessage.id,
        role: apiMessage.role,
        content: apiMessage.message,
        like_option: apiMessage.like_option,
      }))
      .reverse();
  }, [accessToken]);

  const handleReceiveMessage = useCallback(
    async (data) => {
      if (data === "init-response") {
        setMessages((prevMessages) => [
          ...prevMessages,
          { role: "assistant", content: "" },
        ]);
        setShowFirstMessageSkeleton(false);
      } else if (data === "incomplete-response") {
        setIsMessageIncomplete(true);
      } else if (data !== "end-response") {
        setMessages((prevMessages) => {
          const newMessages = [...prevMessages];
          const lastMessage = newMessages[newMessages.length - 1];
          if (lastMessage && lastMessage.role === "assistant") {
            lastMessage.content += data;
          }
          return newMessages;
        });
      } else {
        // data === "end-response"
        if (pendingNavigate && tempChatId) {
          setEnabledTextArea(true);
          navigate(`/chat/${projectId}/${tempChatId}`, { replace: true });
          setPendingNavigate(false);
          setTempChatId(null);
        } else {
          let currentChatId = chatId;
          if (!currentChatId && tempChatId) {
            currentChatId = tempChatId;
          }
          if (currentChatId) {
            try {
              const fetchedMessages = await getMessagesByChat(currentChatId);
              setMessages(fetchedMessages);
            } catch (error) {
              console.error(
                "Erro ao buscar mensagens após end-response:",
                error
              );
            }
          }
          setEnabledTextArea(true);
        }
      }
    },
    [pendingNavigate, tempChatId, navigate, projectId, chatId, getMessagesByChat]
  );

  const handleSetFileContent = async (content) => {
    setFileContent(content);
  };

  // Mantém o ref sempre atualizado com a versão mais recente do callback
  useEffect(() => {
    handleReceiveMessageRef.current = handleReceiveMessage;
  }, [handleReceiveMessage]);

  useEffect(() => {
    const messageEvent = `message-${projectId}`;
    const statusEvent = `status-${projectId}`;

    connect(accessToken, window);

    // Usa ref para que o listener do socket seja estável e não precise re-inscrever
    const unsubscribeMessage = onMessage(messageEvent, (data) => {
      handleReceiveMessageRef.current?.(data);
    });

    // Listener de mensagens de status
    const unsubscribeStatus = onMessage(statusEvent, (data) => {
      console.log("STATUS do servidor:", data);
      handleReceiveStatus(data);
    });

    return () => {
      unsubscribeMessage();
      unsubscribeStatus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, onMessage, connect, accessToken]);
  

  useEffect(() => {
    let isActive = true;

    const fetchData = async () => {
      if (chatId && isActive && !isInitialized && accessToken) {
        try {
          const fetchedMessages = await getMessagesByChat(chatId);
          if (isActive && fetchedMessages) {
            setMessages(fetchedMessages);
            setIsInitialized(true);
          }
        } catch (error) {
          if (isActive) {
            navigate("/chats/404");
          }
          console.error("Error fetching messages for chatId:", chatId, error);
        }
      }
    };

    fetchData();

    return () => {
      isActive = false;
    };
  }, [chatId, isInitialized, accessToken, getMessagesByChat, navigate]);

  useEffect(() => {
    if (chatId) {
      setIsInitialized(false);
    } else {
      setIsInitialized(false);
      setMessages([]);
    }
  }, [chatId]);

  const handleReceiveStatus = useCallback((statusText) => {
    setMessages((prevMessages) => {
      if (prevMessages.length === 0) return prevMessages;
      const newMessages = [...prevMessages];
  
      // Pegamos a última mensagem do array.
      const lastMsgIndex = newMessages.length - 1;
  
      // Garante que exista o array statusUpdates
      if (!newMessages[lastMsgIndex].statusUpdates) {
        newMessages[lastMsgIndex].statusUpdates = [];
      }
  
      // Adiciona o novo status
      newMessages[lastMsgIndex].statusUpdates.push(statusText);
      return newMessages;
    });
  }, []);
  

  const OnLikeButtonPress = (like, messageId) => {
    setMessages((prevMessages) =>
      prevMessages.map((msg) => {
        if (msg.id === messageId) {
          return {
            ...msg,
            like_option: like ? "like" : "dislike",
          };
        }
        return msg;
      })
    );

    SendLikeAction(like ? "like" : "dislike", messageId);
  };

  const SendLikeAction = async (like, messageId) => {
    // TODO: Padronizar o uso de axios para requisições
    const response = await fetch(`${authUrl}/chats/likeAction`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        like,
        messageId,
      }),
    });

    if (!response.ok) {
      throw new Error("Erro ao enviar ação de like");
    }

    const data = await response.json();
    return data.chatId;
  };

  return (
    <div>
      {Chat && (
        <Chat
          projectId={projectId}
          overlay={overlay}
          codeInput={codeInput}
          fileInput={fileInput}
          diff={diff}
          onSendMessage={handleSendMessage}
          handleSetFileContent={handleSetFileContent}
          isInitialized={isInitialized}
          messages={messages}
          enabledTextArea={enabledTextArea}
          setEnabledTextArea={setEnabledTextArea}
          showFirstMessageSkeleton={showFirstMessageSkeleton}
          isMessageIncomplete={isMessageIncomplete}
          handleSendContinueMessage={handleSendContinueMessage}
          chatId={chatId}
          OnLikeButtonPress={OnLikeButtonPress}
        />
      )}
    </div>
  );
};

export default DynamicChats;
